import React, { useEffect, useState } from 'react';
import { Form, Input, InputNumber, Modal, Select, Switch, message } from 'antd';
import { modelApi } from '../../services/api';
import { ModelConfigItem } from '../../services/types';

interface Props {
    open: boolean;
    onCancel: () => void;
    onSuccess: () => void;
    editData?: ModelConfigItem;
}

const providerOptions = [
    { label: 'OpenAI', value: 'openai' },
    { label: 'DeepSeek', value: 'deepseek' },
    { label: 'SiliconFlow', value: 'siliconflow' },
    { label: '本地模型', value: 'local' },
    { label: '自定义', value: 'custom' },
];

const ModelFormModal: React.FC<Props> = ({ open, onCancel, onSuccess, editData }) => {
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [modelType, setModelType] = useState<string>('llm');
    const isEdit = !!editData;

    useEffect(() => {
        if (open) {
            if (editData) {
                form.setFieldsValue({
                    ...editData,
                    api_key: '',
                    is_active: editData.is_active ?? true,
                });
                setModelType(editData.model_type);
            } else {
                form.resetFields();
                form.setFieldsValue({ model_type: 'llm', is_active: true });
                setModelType('llm');
            }
        }
    }, [open, editData, form]);

    const handleOk = async () => {
        try {
            const values = await form.validateFields();
            setLoading(true);

            if (modelType !== 'llm') {
                // delete values.some_llm_param if needed
            }

            if (isEdit) {
                const payload: Record<string, any> = {};
                Object.entries(values).forEach(([k, v]) => {
                    if (v !== undefined && v !== '' && v !== null) {
                        if (k === 'api_key' && !v) return; // skip empty password
                        payload[k] = v;
                    }
                });
                // Don't send model_type on update
                delete payload.model_type;
                await modelApi.update(editData!.id, payload);
                message.success('更新成功');
            } else {
                await modelApi.create(values);
                message.success('创建成功');
            }
            onSuccess();
        } catch (error: any) {
            if (error.response?.data?.detail) {
                message.error(error.response.data.detail);
            } else if (!error.errorFields) {
                message.error('操作失败');
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <Modal
            title={isEdit ? '编辑模型配置' : '添加模型配置'}
            open={open}
            onOk={handleOk}
            onCancel={onCancel}
            confirmLoading={loading}
            destroyOnClose
            width={580}
            okText="保存"
            cancelText="取消"
        >
            <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
                <Form.Item label="配置名称" name="name" rules={[{ required: true, message: '请输入名称' }]}>
                    <Input placeholder="如：DeepSeek Chat" />
                </Form.Item>

                <div style={{ display: 'flex', gap: 16 }}>
                    <Form.Item label="模型类型" name="model_type" rules={[{ required: true }]} initialValue="llm" style={{ flex: 1 }}>
                        <Select
                            options={[
                                { label: 'LLM 大模型', value: 'llm' },
                                { label: 'Embedding 嵌入模型', value: 'embedding' },
                            ]}
                            onChange={(v: string) => {
                                setModelType(v);
                            }}
                            disabled={isEdit}
                        />
                    </Form.Item>

                    <Form.Item label="供应商" name="provider" rules={[{ required: true }]} style={{ flex: 1 }}>
                        <Select options={providerOptions} placeholder="选择供应商" />
                    </Form.Item>
                </div>

                <Form.Item label="模型名称标识" name="model_name" rules={[{ required: true, message: '请输入模型名称' }]}>
                    <Input placeholder="如：deepseek-chat, BAAI/bge-large-zh-v1.5" />
                </Form.Item>

                <div style={{ display: 'flex', gap: 16 }}>
                    <Form.Item label="API Base URL" name="api_base" style={{ flex: 1 }}>
                        <Input placeholder="https://api.deepseek.com/v1" />
                    </Form.Item>
                    <Form.Item label={isEdit ? 'API Key（不修改留空）' : 'API Key'} name="api_key" style={{ flex: 1 }}>
                        <Input.Password placeholder={isEdit ? '留空不修改' : '请输入 API Key'} />
                    </Form.Item>
                </div>

                {modelType === 'llm' && (
                    <>
                        <div style={{ display: 'flex', gap: 16 }}>
                            <Form.Item label="Max Tokens" name="max_tokens" style={{ flex: 1 }}>
                                <InputNumber min={1} max={128000} style={{ width: '100%' }} placeholder="4096" />
                            </Form.Item>
                            <Form.Item label="Temperature" name="temperature" style={{ flex: 1 }}>
                                <InputNumber min={0} max={2} step={0.1} style={{ width: '100%' }} placeholder="0.7" />
                            </Form.Item>
                            <Form.Item label="Top-P" name="top_p" style={{ flex: 1 }}>
                                <InputNumber min={0} max={1} step={0.1} style={{ width: '100%' }} placeholder="0.9" />
                            </Form.Item>
                        </div>

                        <div style={{ display: 'flex', gap: 16 }}>
                            <Form.Item label="是否启用" name="is_active" valuePropName="checked" style={{ flex: 1 }}>
                                <Switch checkedChildren="启用" unCheckedChildren="禁用" />
                            </Form.Item>
                        </div>
                    </>
                )}

                {modelType === 'embedding' && (
                    <>
                        <div style={{ display: 'flex', gap: 16 }}>
                            <Form.Item label="向量维度" name="vector_size" style={{ flex: 1 }}>
                                <InputNumber min={1} style={{ width: '100%' }} placeholder="1024" />
                            </Form.Item>
                            <Form.Item label="最大输入长度" name="max_length" style={{ flex: 1 }}>
                                <InputNumber min={1} style={{ width: '100%' }} placeholder="512" />
                            </Form.Item>
                        </div>

                        <Form.Item label="是否启用" name="is_active" valuePropName="checked">
                            <Switch checkedChildren="启用" unCheckedChildren="禁用" />
                        </Form.Item>
                    </>
                )}

                <Form.Item label="描述" name="description">
                    <Input.TextArea rows={2} placeholder="可选描述" />
                </Form.Item>
            </Form>
        </Modal>
    );
};

export default ModelFormModal;
